import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiRequest } from '../../service/apiRequest';
import { MasterState, initialState } from '../../types/MasterState';
import { Categories, Employee, Wood, WoodType, Ledger, Products, UnitType, BrandNames, Size, GST } from '../../types/CreateProductType';

//
// 🔹 1. BULK FETCH (Initial Load)
//
export const fetchMasterData = createAsyncThunk(
    'master/fetchMasterData',
    async () => {
        const [
            categoriesData,
            employeeData,
            ledgerData,
            productData,
            unittypeData,
            brandnameData,
            sizeData,
            gstData
        ] = await Promise.all([
            apiRequest<Categories[]>({ method: 'get', url: 'category?status=1' }),
            apiRequest<Employee[]>({ method: 'get', url: 'employees?status=1' }),
            apiRequest<{ ledgers: Ledger[] }>({ method: 'get', url: 'ledger-data?status=1' }),
            apiRequest<{ data: Products[] }>({ method: 'get', url: 'get-all-medical-products?status=1' }),
            apiRequest<UnitType[]>({ method: 'get', url: 'unit-types?status=1' }),
            apiRequest<BrandNames[]>({ method: 'get', url: 'brand?status=1' }),
            apiRequest<Size[]>({ method: 'get', url: 'sizes?status=1' }),
            apiRequest<GST[]>({ method: 'get', url: 'gsts?status=1' })
        ]);
        console.log('Master data fetched:', employeeData);
        return {
            categories: categoriesData,
            employees: employeeData,
            ledgers: ledgerData.ledgers,
            products: productData.data,
            unittypes: unittypeData,
            brandnames: brandnameData,
            sizes: sizeData,
            gsts: gstData,
        };
    }
);

//
// 🔹 2. INDIVIDUAL DATASET THUNKS (For selective reload)
//
export const fetchCategory = createAsyncThunk(
    'master/fetchCategory',
    async () =>
        await apiRequest<Categories[]>({ method: 'get', url: 'category?status=1' })
);
export const fetchEmployeeData = createAsyncThunk(
    'master/fetchEmployeeData',
    async () =>
        await apiRequest<Employee[]>({ method: 'get', url: 'employees?status=1' })
);
export const fetcLedgerData = createAsyncThunk(
    'master/fetcLedgerData',
    async () =>
        await apiRequest<{ ledgers: Ledger[] }>({ method: 'get', url: 'ledger-data?status=1' })
);
export const fetcProductData = createAsyncThunk(
    'master/fetcProductData',
    async () =>
        await apiRequest<{ data: Products[] }>({ method: 'get', url: 'get-all-medical-products?status=1' })
);
export const fetcunittypeData = createAsyncThunk(
    'master/fetcunittypeData',
    async () =>
        await apiRequest<{ data: UnitType[] }>({ method: 'get', url: 'unit-types?status=1' })
);
export const fetchbrandnamesData = createAsyncThunk(
    'master/fetchbrandnamesData',
    async () =>
        await apiRequest<{ data: BrandNames[] }>({ method: 'get', url: 'brand?status=1' })
);
export const fetchsizeData = createAsyncThunk(
    'master/fetchsizeData',
    async () =>
        await apiRequest<Size[]>({ method: 'get', url: 'sizes?status=1' })
);
export const fetchgstsData = createAsyncThunk(
    'master/fetchgstsData',
    async () =>
        await apiRequest<GST[]>({ method: 'get', url: 'gsts?status=1' })
);


//
// 🔹 3. SLICE
//
const masterSlice = createSlice({
    name: 'master',
    initialState,
    reducers: {
        resetMaster: () => initialState, // useful on logout
    },
    extraReducers: (builder) => {
        builder

            // ===== BULK LOAD =====
            .addCase(fetchMasterData.pending, (state) => {
                state.loading = true;
            })
            .addCase(
                fetchMasterData.fulfilled,
                (state, action: PayloadAction<Omit<MasterState, 'loading' | 'loaded'>>) => {
                    Object.assign(state, action.payload);
                    state.loading = false;
                    state.loaded = true;
                }
            )
            .addCase(fetchMasterData.rejected, (state) => {
                state.loading = false;
            })
            // ===== INDIVIDUAL RELOADS =====
            .addCase(fetchCategory.fulfilled, (state, action) => {
                state.categories = action.payload;
            })
            .addCase(fetchEmployeeData.fulfilled, (state, action) => {
                state.employees = action.payload;
            })
            .addCase(fetcLedgerData.fulfilled, (state, action) => {
                state.ledgers = action.payload.ledgers;
            })
            .addCase(fetcProductData.fulfilled, (state, action) => {
                state.products = action.payload.data;
            })
            .addCase(fetcunittypeData.fulfilled, (state, action) => {
                state.unittypes = action.payload.data;
            })
            .addCase(fetchbrandnamesData.fulfilled, (state, action) => {
                state.brandnames = action.payload.data;
            })
            .addCase(fetchsizeData.fulfilled, (state, action) => {
                state.sizes = action.payload;
            })
            .addCase(fetchgstsData.fulfilled, (state, action) => {
                state.gsts = action.payload;
            });
    },
});

export const { resetMaster } = masterSlice.actions;
export default masterSlice.reducer;